
import React, { useState, useRef, useEffect } from 'react'
import { cn } from '@/lib/utils'

interface InlineEditProps {
  value: string | null | undefined
  onSave: (newValue: string) => Promise<void>
  placeholder?: string
  className?: string
  disabled?: boolean
  type?: 'text' | 'number'
}

export function InlineEdit({ 
  value, 
  onSave, 
  placeholder = '', 
  className,
  disabled = false,
  type = 'text'
}: InlineEditProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value || '')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setEditValue(value || '')
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    if (isSaving) return

    const trimmedValue = editValue.trim()
    
    if (trimmedValue === (value || '')) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await onSave(trimmedValue)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save:', error)
      setEditValue(value || '')
      setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      setEditValue(value || '')
      setIsEditing(false)
    }
  }

  const handleBlur = () => {
    if (!isSaving) {
      handleSave()
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && !isEditing && !isSaving) {
      console.log('Starting edit mode for value:', value)
      setIsEditing(true)
    }
  }

  const displayValue = value || placeholder || '—'

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={cn(
          "bg-transparent border border-blue-300 outline-none p-1 m-0 text-inherit font-inherit w-full rounded-md",
          "focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          className
        )}
        disabled={isSaving}
        placeholder={placeholder}
      />
    )
  }

  return (
    <span
      onClick={handleClick}
      className={cn(
        "cursor-pointer hover:bg-blue-50 px-1 py-1 rounded-md transition-colors min-w-[40px] inline-block border border-transparent",
        !value && "text-gray-400 italic",
        disabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
        !disabled && "hover:border-blue-200",
        className
      )}
      title={disabled ? "Cannot edit" : "Click to edit"}
    >
      {displayValue}
    </span>
  )
}
